/**
 * Asha OS2 Design Tokens
 * Inspired by BRIX AI pitch deck aesthetic — clean, airy, blue-dominant SaaS style.
 *
 * Use these tokens when building new slides to ensure visual consistency.
 */

export const colors = {
  // Primary blue palette
  primary: {
    50: '#EFF6FF',
    100: '#DBEAFE',
    200: '#BFDBFE',
    300: '#93C5FD',
    400: '#60A5FA',
    500: '#3B82F6',
    600: '#2563EB',
    700: '#1D4ED8',
    800: '#1E40AF',
    900: '#1E3A8A',
  },

  // Backgrounds
  bg: {
    page: '#F8FAFC',         // Overall page background
    slide: '#FFFFFF',         // Default slide background
    slideAlt: '#F1F5F9',     // Alternate light slide background
    card: '#FFFFFF',          // Card backgrounds
    cardHover: '#F8FAFC',    // Card hover state
    sidebar: '#FFFFFF',       // Sidebar background
    dark: '#0F172A',          // Dark accent backgrounds (pricing, emphasis)
    darkCard: '#1E293B',      // Cards on dark backgrounds
    blueGradient: 'linear-gradient(135deg, #2563EB 0%, #3B82F6 50%, #60A5FA 100%)',
    blueGradientSubtle: 'linear-gradient(135deg, #EFF6FF 0%, #DBEAFE 100%)',
  },

  // Text colors
  text: {
    heading: '#0F172A',       // Primary headings
    body: '#475569',          // Body text
    muted: '#94A3B8',         // Muted/secondary text
    label: '#2563EB',         // Section labels, accents
    onDark: '#FFFFFF',        // Text on dark backgrounds
    onDarkMuted: '#94A3B8',   // Muted text on dark backgrounds
    onBlue: '#FFFFFF',        // Text on blue backgrounds
    onBlueMuted: '#BFDBFE',   // Muted text on blue backgrounds
  },

  // Borders
  border: {
    light: '#E2E8F0',        // Standard borders
    lighter: '#F1F5F9',      // Very subtle borders
    blue: '#BFDBFE',         // Blue accent borders
    dark: 'rgba(255,255,255,0.1)', // Borders on dark backgrounds
  },

  // Accent colors (use sparingly)
  accent: {
    emerald: '#10B981',
    amber: '#F59E0B',
    red: '#EF4444',
    purple: '#8B5CF6',
  },
};

export const shadows = {
  sm: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
  card: '0 1px 3px 0 rgba(0, 0, 0, 0.08), 0 1px 2px -1px rgba(0, 0, 0, 0.05)',
  cardHover: '0 4px 12px -2px rgba(0, 0, 0, 0.08), 0 2px 4px -2px rgba(0, 0, 0, 0.04)',
  elevated: '0 10px 25px -5px rgba(0, 0, 0, 0.08), 0 4px 6px -4px rgba(0, 0, 0, 0.04)',
  soft: '0 20px 50px -12px rgba(0, 0, 0, 0.06)',
  slideContainer: '0 4px 24px -4px rgba(0, 0, 0, 0.08), 0 0 0 1px rgba(0, 0, 0, 0.03)',
};

// Tailwind class presets for consistent styling
export const tw = {
  // Typography
  sectionLabel: 'text-sm font-semibold tracking-[0.15em] uppercase text-blue-600',
  sectionLabelOnDark: 'text-sm font-semibold tracking-[0.15em] uppercase text-blue-400',
  sectionLabelOnBlue: 'text-sm font-semibold tracking-[0.15em] uppercase text-blue-200',
  heading1: 'text-5xl md:text-7xl font-bold tracking-tight text-slate-900',
  heading1OnDark: 'text-5xl md:text-7xl font-bold tracking-tight text-white',
  heading2: 'text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight text-slate-900',
  heading3: 'text-xl md:text-2xl font-semibold text-slate-900',
  body: 'text-base text-slate-500 leading-relaxed',
  bodyLarge: 'text-lg md:text-xl text-slate-500 leading-relaxed',

  // Layout
  card: 'bg-white rounded-2xl border border-slate-200/60 shadow-[0_1px_3px_rgba(0,0,0,0.08)]',
  cardHover: 'bg-white rounded-2xl border border-slate-200/60 shadow-[0_1px_3px_rgba(0,0,0,0.08)] hover:shadow-[0_4px_12px_rgba(0,0,0,0.08)] transition-shadow duration-300',
  darkCard: 'bg-slate-800/50 rounded-2xl border border-white/10',
  badge: 'inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-blue-50 text-blue-600 text-sm font-semibold border border-blue-100',
  badgeOnDark: 'inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-blue-500/10 text-blue-400 text-sm font-semibold border border-blue-500/20',

  // Decorative
  dotGrid: "bg-[radial-gradient(#cbd5e1_1px,transparent_1px)] bg-[size:24px_24px]",
  subtleDotGrid: "bg-[radial-gradient(#e2e8f0_0.5px,transparent_0.5px)] bg-[size:20px_20px]",
};

// Animation presets for framer-motion
export const animations = {
  fadeUp: {
    initial: { opacity: 0, y: 20 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.6, ease: 'easeOut' },
  },
  fadeIn: {
    initial: { opacity: 0 },
    animate: { opacity: 1 },
    transition: { duration: 0.5 },
  },
  slideLeft: {
    initial: { opacity: 0, x: 20 },
    animate: { opacity: 1, x: 0 },
    transition: { duration: 0.5, ease: 'easeOut' },
  },
  slideRight: {
    initial: { opacity: 0, x: -20 },
    animate: { opacity: 1, x: 0 },
    transition: { duration: 0.5, ease: 'easeOut' },
  },
  stagger: (index, delay = 0.1) => ({
    initial: { opacity: 0, y: 15 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.4, delay: index * delay, ease: 'easeOut' },
  }),
};

// Slide dimensions
export const slideDimensions = {
  width: 1400,
  height: 787.5,
  aspectRatio: '16/9',
};
